import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDotsVertical,
  IconEdit,
  IconKey,
  IconPlus,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeleteUser, useUsers } from './usersApi';
import { useRoles } from '../roles/rolesApi';
import { UserFormDrawer } from './UserFormDrawer';
import { ChangePasswordModal } from './ChangePasswordModal';
import type { UserDto } from './types';

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { data: users, isLoading } = useUsers();
  const { data: roles } = useRoles();
  const deleteMutation = useDeleteUser();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [deleting, setDeleting] = useState<UserDto | null>(null);
  const [pwChange, setPwChange] = useState<UserDto | null>(null);

  // Build a roleId → label map once so each row's role badges are O(1).
  const roleLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (roles ?? []).forEach((r) =>
      map.set(r.roleId, i18n.language === 'ar' ? r.nameAr : (r.nameEn ?? r.nameAr)),
    );
    return map;
  }, [roles, i18n.language]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (user: UserDto) => {
    setEditing(user);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.userId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.fullNameAr,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasUsers = (users?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.organization')}
        title={t('users.page.title')}
        subtitle={t('users.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('users.page.create')}
          </Button>
        }
      />

      <Paper p={hasUsers ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasUsers ? (
          <EmptyState
            icon={<IconUsers size={22} stroke={1.6} />}
            title={t('users.empty.title')}
            description={t('users.empty.body')}
            actionLabel={t('users.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('users.columns.name')}</Table.Th>
                <Table.Th>{t('users.columns.contact')}</Table.Th>
                <Table.Th>{t('users.columns.roles')}</Table.Th>
                <Table.Th>{t('users.columns.lastLogin')}</Table.Th>
                <Table.Th>{t('users.columns.status')}</Table.Th>
                <Table.Th style={{ width: 60 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users!.map((user) => (
                <Table.Tr key={user.userId}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={32} radius="xl" color="wheels">
                        {initials(user.fullNameAr)}
                      </Avatar>
                      <div>
                        <Text fw={500} c="var(--wheels-ink-strong)">
                          {user.fullNameAr}
                        </Text>
                        {user.fullNameEn && (
                          <Text fz="xs" c="var(--wheels-ink-muted)">
                            {user.fullNameEn}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      {user.email && (
                        <Text fz="sm" c="var(--wheels-ink)">
                          {user.email}
                        </Text>
                      )}
                      {user.phoneNumber && (
                        <Text fz="xs" c="var(--wheels-ink-muted)" className="font-numeric">
                          {user.phoneNumber}
                        </Text>
                      )}
                      {!user.email && !user.phoneNumber && (
                        <Text fz="sm" c="var(--wheels-ink-muted)">
                          —
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {user.roleIds.length === 0 ? (
                      <Text fz="sm" c="var(--wheels-ink-muted)">
                        —
                      </Text>
                    ) : (
                      <Group gap={4}>
                        {user.roleIds.map((roleId) => (
                          <Badge key={roleId} variant="light" color="gray" radius="sm">
                            {roleLabelById.get(roleId) ?? roleId}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="var(--wheels-ink-muted)" className="font-numeric">
                      {user.lastLoginAt
                        ? dayjs(user.lastLoginAt)
                            .locale(i18n.language === 'ar' ? 'ar' : 'en')
                            .format('YYYY-MM-DD HH:mm')
                        : t('users.never')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {user.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <Tooltip label={t('common.actions')} withinPortal>
                          <ActionIcon variant="subtle" color="gray" aria-label={t('common.actions')}>
                            <IconDotsVertical size={16} stroke={1.6} />
                          </ActionIcon>
                        </Tooltip>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} stroke={1.6} />}
                          onClick={() => openEdit(user)}
                        >
                          {t('common.edit')}
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconKey size={14} stroke={1.6} />}
                          onClick={() => setPwChange(user)}
                        >
                          {t('users.actions.changePassword')}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} stroke={1.6} />}
                          onClick={() => setDeleting(user)}
                        >
                          {t('common.delete')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <UserFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={editing}
      />

      <ChangePasswordModal
        opened={pwChange !== null}
        onClose={() => setPwChange(null)}
        user={pwChange}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('users.delete.title')}
        message={t('users.delete.body', { name: deleting?.fullNameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}
